import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const flowSteps = [
  { label: '案例实体', icon: 'ri-stack-line' },
  { label: '问题入口', icon: 'ri-question-answer-line' },
  { label: '理论命题', icon: 'ri-lightbulb-line' },
  { label: '价值落点', icon: 'ri-heart-line' },
  { label: '权威资料', icon: 'ri-folder-shield-line' },
];

const cases = [
  {
    code: '本科03',
    title: '案例强、认知与互动偏弱',
    data: '案例转化83（最强项）；认知规律62，互动广度40、互动深度45',
    note: '观测点已核：数据纠正了先入为主的文字叙述，案例转化突出但认知铺垫和互动仍有提升空间',
    accent: true,
  },
  {
    code: '高职01',
    title: '案例扎实但学生距离候选',
    data: '待ASR核对具体案例名称',
    note: '政策、法治或重大工程案例本身扎实，但需判断教师是否建立了学生能够理解的问题入口',
    accent: false,
  },
  {
    code: '高职04',
    title: '鲜活案例与理论框架边界候选',
    data: '待补证据',
    note: '现实感强的案例仍需进入完整理论框架，从具体冲突到高质量发展整体框架',
    accent: false,
  },
];

export default function P33CaseTransform({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-[11px] font-body font-semibold">
              <i className="ri-scan-line text-xs" /> 技术点六
            </span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            案例转化与<span className="text-primary-600">权威资料关联</span>
          </h2>
        </div>

        {/* 流程 */}
        <div className="max-w-5xl mx-auto mb-6 transition-all duration-1000 delay-200">
          <div className="flex flex-wrap items-center justify-center gap-x-1.5 gap-y-2">
            {flowSteps.map((step, i) => (
              <div key={step.label} className="flex items-center gap-0">
                <div className="flex items-center gap-1.5 px-3 py-2 rounded-xl glass-liquid border-primary-200/30">
                  <span className="font-mono text-[10px] font-bold text-primary-400">{i + 1}</span>
                  <i className={`${step.icon} text-sm text-primary-500`} />
                  <span className="text-xs font-body font-semibold text-foreground-700 whitespace-nowrap">{step.label}</span>
                </div>
                {i < flowSteps.length - 1 && (
                  <i className="ri-arrow-right-s-line text-foreground-300 mx-0.5 text-sm" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 三列案例 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-5 transition-all duration-1000 delay-400">
          {cases.map((c, i) => (
            <div key={c.code} className={`p-4 rounded-2xl transition-all duration-500 hover:scale-[1.02] ${c.accent ? 'glass-liquid-gold border-accent-200/30' : 'glass-liquid border-primary-200/30'}`} style={{ transitionDelay: `${400 + i * 100}ms` }}>
              <div className="relative z-10">
                <h3 className="font-heading text-sm font-bold text-foreground-800 mb-1.5">{c.code}</h3>
                <p className="text-xs font-body font-semibold text-foreground-700 mb-2">{c.title}</p>
                <div className={`p-2.5 rounded-lg mb-2 ${c.accent ? 'bg-accent-50/50' : 'bg-primary-50/30'}`}>
                  <p className="text-[10px] text-foreground-600 font-body leading-relaxed">{c.data}</p>
                </div>
                <p className="text-[10px] text-foreground-400 font-body leading-relaxed">{c.note}</p>
              </div>
            </div>
          ))}
        </div>

        {/* 底部提示 */}
        <div className="max-w-3xl mx-auto text-center transition-all duration-1000 delay-900">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-xl glass-gold border-accent-200/30">
            <i className="ri-lightbulb-flash-line text-sm text-accent-500" />
            <span className="text-xs font-body text-foreground-500">
              案例使用不能只看数量和热闹程度，要看是否提出真正的问题、是否推动理论解释
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}